/**
 * Match mobile post upload: HEIC/HEIF never lands on S3 — iOS
 * `PostImageUploadPreparer` always re-encodes to JPEG (q0.92) before upload.
 * Pipeline Lab accepts desktop/photos-app HEIC picks; convert client-side so
 * the moderation path sees the same `image/jpeg` bytes mobile produces.
 */

const JPEG_QUALITY = 0.92;

export function isHeicLike(file: File): boolean {
  const type = (file.type || '').toLowerCase();
  if (type === 'image/heic' || type === 'image/heif' || type === 'image/heic-sequence') {
    return true;
  }
  const name = file.name.toLowerCase();
  return name.endsWith('.heic') || name.endsWith('.heif');
}

function jpegFileName(original: string): string {
  const base = original.replace(/\.[^.]+$/, '') || 'upload';
  return `${base}.jpg`;
}

/**
 * Returns a File ready for Pipeline Lab upload/preview. Non-HEIC files are
 * returned unchanged. HEIC/HEIF become `image/jpeg` (same quality as mobile).
 */
export async function prepareLabUploadMedia(file: File): Promise<File> {
  if (!isHeicLike(file)) {
    return file;
  }

  const heic2any = (await import('heic2any')).default;
  const converted = await heic2any({
    blob: file,
    toType: 'image/jpeg',
    quality: JPEG_QUALITY,
  });
  const blob = Array.isArray(converted) ? converted[0] : converted;
  if (!blob) {
    throw new Error('HEIC conversion produced no image');
  }
  return new File([blob], jpegFileName(file.name), {
    type: 'image/jpeg',
    lastModified: Date.now(),
  });
}
